import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, orderBy, limit, startAfter, DocumentData, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Eye, PenSquare, Wallet } from 'lucide-react';
import EditClientModal from '../../components/marketplace/EditClientModal';
import WalletModal from '../../components/wallet/WalletModal';
import { useAuth } from '../../contexts/AuthContext';

interface ClientData {
  id: string;
  email: string;
  displayName: string;
  uid?: string;
  role: 'cliente' | 'customer';
  createdAt: Timestamp | string;
  updatedAt?: string;
  walletBalance: number;
  walletId?: string;
  approvalStatus?: string;
  photoBase64?: string;
}

interface WalletClient {
  userId: string;
  companyId: string;
  balance: number;
  name: string;
  email: string;
}

export default function MarketplaceClients() {
  const [allClients, setAllClients] = useState<ClientData[]>([]);
  const [filteredClients, setFilteredClients] = useState<ClientData[]>([]);
  const [displayedClients, setDisplayedClients] = useState<ClientData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
  const [selectedWalletClient, setSelectedWalletClient] = useState<WalletClient | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const ITEMS_PER_PAGE = 25;
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadClients();
    } else {
      navigate('/');
    }
  }, [currentUser]);

  useEffect(() => {
    // Filter clients based on search query
    const filtered = allClients.filter(client => 
      client.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort by date
    const sorted = [...filtered].sort((a, b) => {
      const dateA = a.createdAt instanceof Timestamp ? a.createdAt.toDate() : new Date(a.createdAt as string);
      const dateB = b.createdAt instanceof Timestamp ? b.createdAt.toDate() : new Date(b.createdAt as string);
      return dateB.getTime() - dateA.getTime();
    });

    setFilteredClients(sorted);
    setTotalPages(Math.ceil(sorted.length / ITEMS_PER_PAGE));
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchQuery, allClients]);

  useEffect(() => {
    // Update displayed clients based on current page
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setDisplayedClients(filteredClients.slice(startIndex, endIndex));
  }, [currentPage, filteredClients]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      
      // Get all clients
      const querySnapshot = await getDocs(query(
        usersRef,
        where('role', 'in', ['cliente', 'customer']),
        orderBy('createdAt', 'desc')
      ));

      // Load wallet data for each client
      const clientsData = await Promise.all(
        querySnapshot.docs.map(async (userDoc) => {
          const userData = userDoc.data();
          let walletBalance = 0;
          let walletId = '';

          try {
            const walletDoc = await getDoc(doc(db, 'wallets', userDoc.id));
            if (walletDoc.exists()) {
              const walletData = walletDoc.data();
              walletBalance = Number(walletData.balance || 0);
              walletId = walletDoc.id;
            }
          } catch (err) {
            console.error(`Erro ao carregar carteira do cliente ${userDoc.id}:`, err);
          }

          let createdAt = userData.createdAt;
          if (typeof createdAt === 'string') {
            try {
              createdAt = Timestamp.fromDate(new Date(createdAt));
            } catch (err) {
              console.error('Erro ao converter data:', err);
            }
          }

          return {
            id: userDoc.id,
            email: userData.email || '',
            displayName: userData.displayName || '',
            role: userData.role,
            photoBase64: userData.photoBase64,
            walletBalance,
            walletId,
            createdAt,
            approvalStatus: userData.approvalStatus || ''
          } as ClientData;
        })
      );

      setAllClients(clientsData);
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWalletClick = (client: ClientData) => {
    setSelectedWalletClient({
      userId: client.id,
      companyId: client.id,
      balance: client.walletBalance || 0,
      name: client.displayName,
      email: client.email
    });
    setIsWalletModalOpen(true);
  };

  const handleWalletModalClose = () => {
    setSelectedWalletClient(null);
    loadClients();
  };

  const formatCreatedAt = (createdAt: Timestamp | string | undefined): string => {
    if (!createdAt) return 'Data inválida';

    try {
      const date = createdAt instanceof Timestamp ? createdAt.toDate() : new Date(createdAt);
      
      if (isNaN(date.getTime())) return 'Data inválida';

      // Formatar dia, mês e ano
      const dia = date.getDate().toString().padStart(2, '0');
      const mes = (date.getMonth() + 1).toString().padStart(2, '0');
      const ano = date.getFullYear();
      
      // Formatar hora e minuto
      const hora = date.getHours().toString().padStart(2, '0');
      const minuto = date.getMinutes().toString().padStart(2, '0');

      // Retornar no formato DD/MM/AAAA HH:MM
      return `${dia}/${mes}/${ano} ${hora}:${minuto}`;
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'Data inválida';
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Clientes Marketplace</h1>
        <div className="flex-1 max-w-md ml-4">
          <input
            type="text"
            placeholder="Pesquisar por nome ou email..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div>A carregar...</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data de Registo
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayedClients.map((client) => (
                  <tr key={client.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {client.displayName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCreatedAt(client.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => handleWalletClick(client)}
                          className="text-blue-600 hover:text-blue-900 inline-flex items-center gap-1"
                          title="Ver carteira"
                        >
                          <Wallet className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/dashboard/marketplace/clients/${client.id}`)}
                          className="text-green-600 hover:text-green-900 inline-flex items-center gap-1"
                          title="Ver ficha do cliente"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedClient(client);
                            setIsEditModalOpen(true);
                          }}
                          className="text-orange-600 hover:text-orange-900 inline-flex items-center gap-1"
                          title="Editar cliente"
                        >
                          <PenSquare className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            <div className="mt-4 flex justify-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary/90 text-white'
                }`}
              >
                Anterior
              </button>
              <span className="px-4 py-2 text-sm text-gray-700">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary/90 text-white'
                }`}
              >
                Próxima
              </button>
            </div>
          </div>
        </>
      )}

      {selectedWalletClient && (
        <WalletModal
          isOpen={isWalletModalOpen}
          onClose={handleWalletModalClose}
          userId={selectedWalletClient.userId}
          companyId={selectedWalletClient.companyId}
          currentBalance={selectedWalletClient.balance}
          userName={selectedWalletClient.name}
          userEmail={selectedWalletClient.email}
        />
      )}

      {selectedClient && (
        <EditClientModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedClient(null);
          }}
          client={selectedClient}
        />
      )}
    </div>
  );
}